import {
  LuxButton,
  LuxDivider,
  LuxLoader,
  LuxSelectablePill,
  LuxTypography,
  Stack,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import useCustomTranslation from '@hooks/useCustomTranslation';
import useGetAppointments from '@hooks/useGetAppointments';
import useGetInsuranceConfiguration from '@hooks/useGetInsuranceConfiguration';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { ServicesModal } from './ServicesModal';
import { useState } from 'react';
import { TabletBreakpoint } from '@utils/consts/breakpoints.consts';
import { AppointmentOption } from '@components/AppointmentTypePage/AppointmentTypePage.styles';
import { StepContainer } from '@components/Steps/Steps.styles';
import { CardTitleWrapper } from '@components/BookingDetails/BookingDetails.styles';

export function ServicesCard() {
  const MAX_INSURANCES = 10;
  const MAX_APPOINTMENTS = 10;
  const [openModal, setOpenModal] = useState(false);
  const { t } = useCustomTranslation();
  const theme = useTheme();
  const practiceConfig = useOnlineBookingStore(state => state.generalConfig);
  const organizationId = useOnlineBookingStore(
    state => state.generalInfo?.organizationId,
  );
  const practiceId = useOnlineBookingStore(state => state.practiceId);

  const { data: insurances, isLoading: isLoadingInsurances } =
    useGetInsuranceConfiguration({
      organizationId,
    });

  const { data: appointments, isLoading: isLoadingAppt } = useGetAppointments({
    practiceIdValue: practiceId,
  });

  const isMobile = useMediaQuery(`(max-width:${TabletBreakpoint}px)`);

  return (
    <>
      {(practiceConfig?.practiceInformation?.showExamServices ||
        practiceConfig?.practiceInformation?.showAcceptedInsurances) && (
        <StepContainer>
          <CardTitleWrapper
            borderColor={
              theme.palette.LUX.obTokens.bordersColorNeutralSecondary
            }
          >
            <LuxTypography variant="heading4">
              {t('practiceCard.services')}
            </LuxTypography>
            {!isMobile &&
              practiceConfig?.practiceInformation?.showExamServices && (
                <LuxButton
                  onClick={() => setOpenModal(true)}
                  variant="secondary"
                >
                  {t('practiceCard.viewAllAppointments')}
                </LuxButton>
              )}
          </CardTitleWrapper>
          <Stack padding="0.5rem 1rem 1rem 1rem" gap="1rem">
            {isMobile ? (
              <>
                {practiceConfig?.practiceInformation
                  ?.showAcceptedInsurances && (
                  <Stack flexDirection="column" gap="0.5rem" width="100%">
                    <LuxTypography variant="textMedium">
                      {t('moreInfosModal.acceptedInsurances2')}
                    </LuxTypography>
                    <LuxTypography
                      variant="textXs"
                      style={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }}
                    >
                      {insurances && insurances.length > 0 ? (
                        <>
                          {insurances
                            .slice(0, MAX_INSURANCES)
                            .map(i => i.name)
                            .join('; ')}
                          {insurances.length > MAX_INSURANCES && (
                            <>
                              {' ;'}+{insurances.length - MAX_INSURANCES}{' '}
                              {t('moreInfosModal.acceptedInsurances2')}
                            </>
                          )}
                        </>
                      ) : (
                        t('No insurances')
                      )}
                    </LuxTypography>
                  </Stack>
                )}
                <LuxDivider />
                {practiceConfig?.practiceInformation?.showExamServices && (
                  <Stack flexDirection="column" gap="0.5rem" width="100%">
                    <LuxTypography variant="textMedium">
                      {t('practiceCard.appTypes')}
                    </LuxTypography>
                    <LuxTypography variant="textXs">
                      {appointments && appointments.length > 0 ? (
                        <>
                          {appointments
                            .slice(0, MAX_APPOINTMENTS)
                            .map(i => i.title)
                            .join('; ')}
                          {appointments.length > MAX_APPOINTMENTS && (
                            <>
                              {' ;'}+{appointments.length - MAX_APPOINTMENTS}{' '}
                              {t('practiceCard.appTypes')}
                            </>
                          )}
                        </>
                      ) : (
                        t('No appointments')
                      )}
                    </LuxTypography>
                  </Stack>
                )}
              </>
            ) : (
              <>
                {practiceConfig?.practiceInformation
                  ?.showAcceptedInsurances && (
                  <AppointmentOption
                    style={{ cursor: 'unset' }}
                    selectedColor={
                      theme.palette.LUX.obTokens?.bordersColorBrandPrimary
                    }
                    color={
                      theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
                    }
                    minHeight
                  >
                    <Stack flexDirection="column" gap="0.5rem" width="100%">
                      <LuxSelectablePill
                        text={t('moreInfosModal.acceptedInsurances2')}
                        selectable={false}
                        customColor={
                          theme.palette.LUX.obTokens.bgColorBaseSecondary
                        }
                        customBorderColor={
                          theme.palette.LUX.obTokens
                            .bordersColorNeutralSecondary
                        }
                      />
                      {isLoadingInsurances ? (
                        <LuxLoader size="small" />
                      ) : (
                        <LuxTypography
                          variant="textXs"
                          style={{
                            textAlign: 'left',
                            wordBreak: 'break-word',
                          }}
                        >
                          {insurances && insurances.length > 0 ? (
                            <>
                              {insurances
                                .slice(0, MAX_INSURANCES)
                                .map(i => i.name)
                                .join('; ')}
                              {insurances.length > MAX_INSURANCES && (
                                <>
                                  {'; '}+{insurances.length - MAX_INSURANCES}{' '}
                                  {t('moreInfosModal.acceptedInsurances2')}
                                </>
                              )}
                            </>
                          ) : (
                            t('No insurances')
                          )}
                        </LuxTypography>
                      )}
                    </Stack>
                  </AppointmentOption>
                )}
                {practiceConfig?.practiceInformation?.showExamServices && (
                  <AppointmentOption
                    style={{ cursor: 'unset' }}
                    selectedColor={
                      theme.palette.LUX.obTokens?.bordersColorBrandPrimary
                    }
                    color={
                      theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
                    }
                    minHeight
                  >
                    <Stack flexDirection="column" gap="0.5rem" width="100%">
                      <LuxSelectablePill
                        text={t('practiceCard.appTypes')}
                        selectable={false}
                        customColor={
                          theme.palette.LUX.obTokens.bgColorBaseSecondary
                        }
                        customBorderColor={
                          theme.palette.LUX.obTokens
                            .bordersColorNeutralSecondary
                        }
                      />
                      {isLoadingAppt ? (
                        <LuxLoader size="small" />
                      ) : (
                        <LuxTypography
                          variant="textXs"
                          style={{ textAlign: 'left' }}
                        >
                          {appointments && appointments.length > 0 ? (
                            <>
                              {appointments
                                .slice(0, MAX_APPOINTMENTS)
                                .map(i => i.title)
                                .join('; ')}
                              {appointments.length > MAX_APPOINTMENTS && (
                                <>
                                  {' ;'}+
                                  {appointments.length - MAX_APPOINTMENTS}{' '}
                                  {t('practiceCard.appTypes')}
                                </>
                              )}
                            </>
                          ) : (
                            t('No appointments')
                          )}
                        </LuxTypography>
                      )}
                    </Stack>
                  </AppointmentOption>
                )}
              </>
            )}
            {isMobile &&
              practiceConfig?.practiceInformation?.showExamServices && (
                <LuxButton
                  onClick={() => setOpenModal(true)}
                  variant="secondary"
                >
                  {t('practiceCard.viewAllAppointments')}
                </LuxButton>
              )}
          </Stack>
        </StepContainer>
      )}
      <ServicesModal
        isOpen={openModal}
        appointmentTypes={appointments ?? []}
        onClose={() => setOpenModal(false)}
      />
    </>
  );
}
